import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  Delete,
  LocationOn,
  Star,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const AddressesList = ({
  addresses = [],
  onEdit,
  onDelete,
  onSetDefaultShipping,
  onSetDefaultBilling,
  loading = false,
  selectable = false,
  selectedId = null,
  onSelect = null
}) => {
  const { t } = useTranslation();

  // No need for handleDelete here since parent handles the modal

  const handleSetDefaultShipping = (addressId) => {
    if (onSetDefaultShipping && !loading) {
      onSetDefaultShipping(addressId);
    }
  };

  const handleSetDefaultBilling = (addressId) => {
    if (onSetDefaultBilling && !loading) {
      onSetDefaultBilling(addressId);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>{t('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  if (addresses?.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('profile.noAddresses', 'No addresses found')}
        </Typography>
        {onEdit && (
          <Button variant="contained" onClick={() => onEdit(null)}>
            {t('profile.addFirstAddress', 'Add Your First Address')}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {addresses?.map((address) => (
        <Grid item xs={12} md={6} lg={4} key={address.id}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: selectable && selectedId === address.id ? 2 : 1,
              borderColor: selectable && selectedId === address.id ? 'primary.main' : 'divider',
              position: 'relative'
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              {/* Address Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {address.label || t('profile.address', 'Address')}
                  </Typography>
                  {address.is_default_shipping && (
                    <Chip
                      icon={<LocationOn sx={{ fontSize: 16 }} />}
                      label={t('profile.defaultShipping', 'Default Shipping')}
                      color="primary"
                      size="small"
                      sx={{ mb: 1, mx: 1 }}
                    />
                  )}
                  {address.is_default_billing && (
                    <Chip
                      icon={<Star sx={{ fontSize: 16 }} />}
                      label={t('profile.defaultBilling', 'Default Billing')}
                      color="secondary"
                      size="small"
                      sx={{ mb: 1, mx: 1 }}
                    />
                  )}
                </Box>

                {!selectable && (
                  <Box>
                    {onEdit && (
                      <Tooltip title={t('common.edit', 'Edit')}>
                        <IconButton onClick={() => onEdit(address)} size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title={t('common.delete', 'Delete')}>
                        <IconButton
                          onClick={() => onDelete(address.id)}
                          size="small"
                          color="error"
                          disabled={loading}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>

              {/* Address Details */}
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {address.street}, {address.building}
                  {address.floor && `, ${t('profile.floor', 'Floor')} ${address.floor}`}
                  {address.apartment && `, ${t('profile.apartment', 'Apt')} ${address.apartment}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {address.area}, {address.city}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {address.phone}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {selectable && (
                  <Button
                    size="small"
                    variant={selectedId === address.id ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => onSelect && onSelect(address.id)}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {selectedId === address.id ? t('common.selected', 'Selected') : t('common.select', 'Select')}
                  </Button>
                )}
                {!selectable && !address.is_default_shipping && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSetDefaultShipping(address.id)}
                    disabled={loading}
                    sx={{ minWidth: 140 }}
                  >
                    {loading ? t('common.setting', 'Setting...') : t('profile.setAsDefaultShipping', 'Set as Default Shipping')}
                  </Button>
                )}
                {!address.is_default_billing && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSetDefaultBilling(address.id)}
                    disabled={loading}
                    sx={{ minWidth: 140 }}
                  >
                    {loading ? t('common.setting', 'Setting...') : t('profile.setAsDefaultBilling', 'Set as Default Billing')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default AddressesList;
